<template>
  <payment-link-layout
    v-if="paymentLink"
    ref="payment-link-layout"
    :isLoading="isLoading"
    @finishLoading="onFinishLoading"
  >
    <template v-slot:content>
      <h3 class="gray-900 mt-24 mb-12">Verificação de crédito</h3>
      <p class="small-regular gray-500">
        Para continuar, verifique seu crédito disponível para comprar com o Parcelex.
      </p>

      <form class="mt-24" name="verifyCreditForm">
        <div v-if="paymentLink.customerPhone">
          <p class="small-regular gray-500">Celular</p>
          <p class="small-regular gray-900 mt-4 mb-20">
            {{ paymentLink.customerPhone | phoneFilter }}
          </p>
        </div>

        <div v-if="paymentLink.customerEmail">
          <p class="small-regular gray-500">Email</p>
          <p class="small-regular gray-900 mt-4 mb-20">{{ paymentLink.customerEmail }}</p>
        </div>

        <app-input
          v-if="!paymentLink.customerEmail"
          label="Email"
          class="app-input"
          type="email"
          v-model="user.email"
          @input="handleFormInput('email', ...arguments)"
          :errorMessage="getMessage('email')"
        />

        <app-input
          label="CPF"
          class="app-input"
          v-model="user.cpf"
          @input="handleFormInput('cpf', ...arguments)"
          :errorMessage="getMessage('cpf')"
          v-mask="'###.###.###-##'"
          :inputmode="'numeric'"
        />

        <app-input
          label="CEP"
          class="app-input"
          v-model="user.zipCode"
          @input="handleFormInput('zipCode', ...arguments)"
          :errorMessage="getMessage('zipCode')"
          v-mask="'#####-###'"
          :inputmode="'numeric'"
        />
      </form>
    </template>

    <template v-slot:footer>
      <round-button
        color="primary mt-12"
        @click="handleVerifyCredit"
        content="Continuar"
      />
    </template>
  </payment-link-layout>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mask } from 'vue-the-mask';
import UtilMixin from '@/mixins/util';
import stringMixin from '@/mixins/string';
import PaymentLinkLayout from '../layout/PaymentLinkLayout.vue';
import AppInput from '../../../components/ui/AppInput.vue';
import RoundButton from '../components/RoundButton.vue';

export default {
  name: 'PaymentLinkVerifyCredit',
  components: {
    PaymentLinkLayout,
    AppInput,
    RoundButton,
  },
  directives: {
    mask,
  },
  mixins: [UtilMixin, stringMixin],
  data: () => ({
    isLoading: false,
    applyResponse: {
      inputErrors: {
        email: null,
        zipCode: null,
      },
    },
    formInputs: {
      cpf: {
        isValid: false,
        validateFunction: 'isValidCpf',
        errorMessage: 'CPF inválido',
        currentMessage: '',
      },
      email: {
        isValid: false,
        validateFunction: 'isValidEmail',
        errorMessage: 'Email inválido',
        currentMessage: '',
      },
      zipCode: {
        validateFunction: 'isValidZipCode',
        isValid: false,
        errorMessage: 'CEP inválido',
        currentMessage: '',
      },
    },
  }),
  computed: {
    ...mapState('paymentLink', ['user', 'nextRoute', 'paymentLink']),
    isValidForm() {
      let isValid = true;
      const keys = Object.keys(this.formInputs);
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!this.formInputs[key].isValid) {
          isValid = false;
          break;
        }
      }
      return isValid;
    },
  },
  methods: {
    ...mapActions('paymentLink', ['setUserData', 'verifyCredit']),
    async handleVerifyCredit() {
      if (!this.isValidForm) {
        this.handleErrorMessages();
        return;
      }
      this.isLoading = true;
      const verifyCreditResponse = await this.verifyCredit();
      const { inputError, creditApplyStatus } = verifyCreditResponse || {};

      if (inputError) {
        this.applyResponse.inputErrors[inputError] = true;
        this.$refs['payment-link-layout'].finishLoading({ success: false });
        this.isLoading = false;
        return;
      }

      const creditApplyStatusApproved = creditApplyStatus === 'PRE_APPROVED';
      this.$refs['payment-link-layout'].finishLoading({ success: creditApplyStatusApproved });
    },
    async onFinishLoading() {
      await this.$router.replace({
        name: this.nextRoute,
        query: this.$route.query,
      });
    },
    handleFormInput(field, value) {
      if (!value) return;
      if (this.applyResponse.inputErrors[field]) {
        this.formInputs[field].isValid = false;
        this.handleErrorMessages();
        this.applyResponse.inputErrors[field] = null;
        return;
      }
      this.setUserData({ field, value });
      const { validateFunction } = this.formInputs[field];
      this.formInputs[field].isValid = this[validateFunction](value);
      if (this.formInputs[field].currentMessage !== '') {
        this.formInputs[field].currentMessage = '';
      }
    },
    handleErrorMessages() {
      Object.keys(this.formInputs).forEach((key) => {
        if (!this.formInputs[key].isValid) {
          this.formInputs[key].currentMessage = this.formInputs[key].errorMessage;
        }
      });
    },
    getMessage(field) {
      return this.formInputs[field].currentMessage;
    },
  },
  mounted() {
    if (this.paymentLink && this.paymentLink.customerEmail) {
      this.handleFormInput('email', this.paymentLink.customerEmail);
    }
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../scss/__new-styles.scss';

  .input-error-message {
    @extend .small-regular;
    color: $red-600;
    margin-top: 4px;
  }

  ::v-deep .app-input {
    input {
      display: block;
      width: 100%;
      border-radius: 0.375rem;
      padding: 8px 12px;
      background-color: $gray-50;
      height: 52px;

      @media (min-width: $md) {
        height: 48px;
      }

      &.error-input {
        border: 1px solid var(--gray-300);
      }
    }

    .input-error-message {
      @extend .input-error-message;
    }
  }
</style>
